import { useEffect, useMemo, useState } from "react";

import {
  TRANSCRIPT_LINES_MAX_LIMIT,
  useGetTranscriptLinesQuery,
  useLazyGetTranscriptLinesQuery
} from "services/follow-ups";
import { TranscriptLine } from "services/types/follow-up.type";

/**
 * Pages past the first are fetched automatically, but not without a ceiling.
 *
 * Sized from the only real measurement we have: a 102-minute visit numbers to
 * 2,402 lines (~23.5 a minute, ~4 words each). A two-hour visit — the clinical
 * maximum — is therefore ~2,800 lines, or ~4,500 for a conversation held at a
 * brisk 150wpm rather than that recording's leisurely 95. Three pages is 6,000,
 * so even the fast case has room to spare; two would be 4,000 and could clip a
 * dense two-hour visit, which is why this is not lower.
 *
 * It stays a real backstop because nothing actually stops a long recording:
 * `MAX_RECORDING_DURATION_MS` is only read by the unmounted non-streaming
 * provider and the upload worker, the "Are you still recording?" prompt
 * deliberately never stops one, and a paused visit accumulates across sessions.
 * The cap bounds the damage a runaway or corrupt transcript can do to a modal
 * open; it is not meant to be reached by a real visit.
 *
 * Not tied to the backend's 6,000-line `_MAX_PROMPT_LINES` despite landing on
 * the same number — that one bounds prompt TOKENS, this one bounds a response
 * payload, and that module's own docstring warns against conflating the two.
 */
const MAX_TRANSCRIPT_PAGES = 3;

const EMPTY_LINES: TranscriptLine[] = [];

/** Why the transcript on screen is incomplete. `null` when it is whole. */
export type TranscriptTruncation = "capped" | "failed";

export interface ScribeTranscriptLines {
  lines: TranscriptLine[];
  isLoading: boolean;
  isError: boolean;
  truncation: TranscriptTruncation | null;
}

/**
 * Tail pages are stamped with the scribe they belong to. Both dialogs happen to
 * remount today — the insert dialog keys on the scribe id, and closing the
 * details dialog unmounts it — but `<ScribeDetailsDialog />` is rendered
 * unconditionally from the scribes list and takes its id from redux, so nothing
 * structurally pins it. A page landing after a switch must not splice one
 * visit's lines into another's.
 */
interface TailState {
  forScribeId: number;
  lines: TranscriptLine[];
  hadFailure: boolean;
}

/**
 * The whole transcript for a recording, as numbered, speaker-tagged, timestamped
 * lines. The first page comes from a subscription; anything past it is fetched
 * on demand and appended.
 */
export default function useScribeTranscriptLines(
  scribeId: number
): ScribeTranscriptLines {
  // The Transcript tab is the one tab the parent does not guard on `remote.id`,
  // so `Number(id)` really can be NaN — without this we request `/NaN/`.
  const isValidScribeId = Number.isFinite(scribeId) && scribeId > 0;

  // `currentData`, never `data`: RTK deliberately retains `data` from a previous
  // arg while a new one is in flight, so reading it would render the last
  // recording's transcript under this one's header — with `isLoading` false, so
  // no spinner and no error either.
  const { currentData, isFetching, isError } = useGetTranscriptLinesQuery(
    { ambientScribeId: scribeId, limit: TRANSCRIPT_LINES_MAX_LIMIT, offset: 0 },
    { skip: !isValidScribeId }
  );

  const [fetchPage] = useLazyGetTranscriptLinesQuery();
  const [tail, setTail] = useState<TailState | null>(null);

  const firstPage = currentData?.results;
  const count = currentData?.count ?? 0;

  useEffect(() => {
    const loaded = firstPage?.length ?? 0;
    // `loaded === 0` also guards the offset loop: a server reporting a count
    // with no rows would otherwise make the first offset 0 and re-read page one.
    if (loaded === 0 || loaded >= count) {
      // Clear any earlier verdict. A refetch that now returns the whole
      // transcript must not keep warning that part of it is missing.
      setTail(null);
      return;
    }

    const pagesToFetch = Math.min(
      Math.ceil((count - loaded) / TRANSCRIPT_LINES_MAX_LIMIT),
      MAX_TRANSCRIPT_PAGES - 1
    );
    const offsets = Array.from(
      { length: pagesToFetch },
      (_, index) => loaded + index * TRANSCRIPT_LINES_MAX_LIMIT
    );

    let cancelled = false;
    setTail(null);

    // `allSettled`, not `all`: with `all`, one failing page discards every page
    // that already arrived, so a 6,000-line visit whose last page 500s would
    // throw away 2,000 lines we had already paid for.
    Promise.allSettled(
      offsets.map((offset) =>
        fetchPage(
          {
            ambientScribeId: scribeId,
            limit: TRANSCRIPT_LINES_MAX_LIMIT,
            offset
          },
          true
        ).unwrap()
      )
    ).then((results) => {
      if (cancelled) return;

      // Only the leading contiguous run is usable — a gap in the middle cannot
      // be rendered honestly, so stop at the first failure and report it.
      const fetched: TranscriptLine[] = [];
      let hadFailure = false;
      for (const result of results) {
        if (result.status !== "fulfilled") {
          hadFailure = true;
          break;
        }
        fetched.push(...result.value.results);
      }
      setTail({ forScribeId: scribeId, lines: fetched, hadFailure });
    });

    return () => {
      cancelled = true;
    };
  }, [scribeId, firstPage, count, fetchPage]);

  const matchedTail = tail?.forScribeId === scribeId ? tail : null;

  const lines = useMemo(() => {
    const head = firstPage ?? EMPTY_LINES;
    if (!matchedTail?.lines.length) return head;
    return [...head, ...matchedTail.lines];
  }, [firstPage, matchedTail]);

  return {
    lines,
    isLoading: isFetching && !currentData,
    isError,
    // Measured against what actually arrived, never against the page arithmetic
    // that predicted it. A server whose page size differs from our copy of
    // `max_limit` returns short pages, and predicting from the arithmetic would
    // leave a gap in the middle of the transcript with nothing said about it.
    truncation: matchedTail && lines.length < count
      ? (matchedTail.hadFailure ? "failed" : "capped")
      : null
  };
}
